import { PlusIcon } from "@heroicons/react/24/solid";
import { Button } from "@material-tailwind/react";
import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import Container from "../../Components/UI/Container";
import { useUserStore, useWorkspaceStore } from "../../Context/store";
import useDebounce from "../../Services/useDebounce";
import { getWorkspaceRole } from "../../Utils/Helper";
import { useGetReferenceList } from "../../Web-Hooks/Reference/use-reference";
import { getSubWorkspaceByWorkId } from "../../api/subworkspace/subworkspace";
import SearchFilter from "../Profile/SearchFilter";
import AddReferencePopup from "./AddReferencePopup";
import ReferenceList from "./ReferenceList";
import { useRolePermissions } from "../../hooks/usePermission";

const Reference = () => {
  const { user: loggedInUser } = useUserStore();
  const { selectedWorkspace } = useWorkspaceStore();
  const [search, setSearch] = useState("");
  const debouncedSearchTerm = useDebounce(search, 600);
  const [filter, setFilters] = useState(null);
  const [open, setOpen] = useState(false);
  const [selectedReference, setSelectedReference] = useState(null);
  const { hasPermission } = useRolePermissions();
  const subId = filter?.forum?.label === "Public" ? "" : filter?.forum?.value;
  const workId =
    filter?.forum?.label === "Public"
      ? filter?.forum?.value
      : selectedWorkspace?.workSpaceId;

  const { data: subWorkSpaces } = useQuery({
    queryKey: [selectedWorkspace?.workSpaceId, loggedInUser?.userId],
    queryFn: async () => {
      const res = await getSubWorkspaceByWorkId(
        filter.workspace?.value,
        loggedInUser?.userId
      );
      return res;
    },
    select: (data) => {
      return data.listOfSubworkspaces;
    },
    enabled: !!selectedWorkspace?.workSpaceId && !!loggedInUser?.userId,
  });

  const {
    data,
    isLoading: isReferenceListLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetReferenceList(
    loggedInUser?.userId,
    workId,
    subId,
    debouncedSearchTerm
  );

  const referenceList = data?.pages?.flatMap((page) => page?.referencesList);

  const createForumOptions =
    subWorkSpaces?.map((subWorkspace) => {
      return {
        label: subWorkspace.subWorkSpaceName,
        value: subWorkspace.subWorkSpaceId,
        role: subWorkspace.loginUserRole,
      };
    }) || [];

  const forumOptions = [
    {
      label: "Public",
      value: selectedWorkspace?.workSpaceId,
      role: getWorkspaceRole(selectedWorkspace, loggedInUser?.userId),
    },
    ...createForumOptions,
  ];

  const handleUpdatePopupOpen = (selected) => {
    setOpen(true);
    setSelectedReference(selected);
  };

  useEffect(() => {
    if (!open) {
      setSelectedReference(null);
    }
  }, [open]);

  const hasAccessToCreate = hasPermission(
    loggedInUser?.role,
    "createReference"
  );

  const hasAccessForAction = hasAccessToCreate;

  return (
    <Container>
      <AddReferencePopup
        open={open}
        setOpen={setOpen}
        defaultValues={selectedReference}
        defaultForum={filter?.forum}
      />
      <section className="flex justify-between items-center w-full gap-4">
        <div className="lg:w-[60%]">
          <SearchFilter
            showFilters={["search", "forum"]}
            filters={
              filter || {
                forum: {
                  label: "Public",
                  value: selectedWorkspace?.workSpaceId,
                  role: getWorkspaceRole(
                    selectedWorkspace,
                    loggedInUser?.userId
                  ),
                },
              }
            }
            search={search}
            setSearch={setSearch}
            setFilters={setFilters}
            forumOptions={[...forumOptions]}
          />
        </div>
        {hasAccessForAction && (
          <Button
            id="add-reference"
            variant="filled"
            onClick={() => {
              setSelectedReference(null);
              setOpen(true);
            }}
            className="bg-primary text-debatePalette-title flex gap-2 justify-center items-center px-4 py-2.5 capitalize"
          >
            <PlusIcon className="h-4 w-4" />
            <span className="text-sm">Add</span>
          </Button>
        )}
      </section>
      <section className="h-full w-full overflow-y-auto mt-3">
        <ReferenceList
          user={loggedInUser}
          referenceList={referenceList}
          isReferenceLoading={isReferenceListLoading}
          setSelectedReference={handleUpdatePopupOpen}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
        />
      </section>
    </Container>
  );
};

export default Reference;
